import dayjs from 'dayjs';
import type { ApprovalAttributes } from '@/types/approval';

/** SAP 예산증액 신청(187) — 레거시 appr_edit_forms_eas_new_budget */
export const SAP_BUDGET_INCREASE_TYPE = 187;

export const isSapBudgetIncreaseType = (apvType: number): boolean =>
  apvType === SAP_BUDGET_INCREASE_TYPE;

/** 레거시 kendoNumericTextBox max 9999999999999 */
export const SAP_BUDGET_INCREASE_AMOUNT_MAX_DIGITS = 13;

export const SAP_BUDGET_INCREASE_SAVE_FAILED_MESSAGE =
  '예산증액 저장에 실패하였습니다.';
export const SAP_BUDGET_INCREASE_LOADING_MESSAGE =
  '데이터를 불러오는 중입니다. 잠시 후 다시 시도해주세요.';
export const SAP_BUDGET_INCREASE_ACCOUNT_EMPTY_MESSAGE =
  '예산 증액 계정이 존재하지 않습니다.';
export const SAP_BUDGET_INCREASE_ACCOUNT_ERROR_MESSAGE =
  '예산 증액 계정 로드에 실패하였습니다.';

/** draft-source display.accounts 행 — SAP 원문(숫자도 문자열) */
export type SapBudgetIncreaseAccount = {
  costElement?: string | null;
  accountName?: string | null;
  initialBudget?: string | null;
  increasedBudget?: string | null;
  actualTotal?: string | null;
  remaining?: string | null;
};

export type SapBudgetIncreaseDraftSource = {
  code: number;
  attributes?: { yearMonth?: string };
  display?: { accounts?: SapBudgetIncreaseAccount[] | null };
};

export type SapBudgetIncreaseState = {
  /** 증액 연월 YYYY-MM */
  yearMonth: string;
  accounts: SapBudgetIncreaseAccount[];
  costElement: string;
  /** 증액 신청금액 — 화면 입력값(콤마 포함) */
  amount: string;
  loading?: boolean;
  /** 연월·계정을 직접 바꿈. 진입 자동 조회는 미저장 경고 대상이 아님 */
  userChanged?: boolean;
};

/** 레거시 — 지난달 1일부터 선택 가능 */
export const getSapBudgetIncreaseMinMonth = (now = dayjs()) =>
  now.subtract(1, 'month').startOf('month');

export const formatBudgetAmountInput = (value: string): string => {
  const digits = value
    .replace(/\D/g, '')
    .replace(/^0+/, '')
    .slice(0, SAP_BUDGET_INCREASE_AMOUNT_MAX_DIGITS);
  return digits ? Number(digits).toLocaleString('en-US') : '';
};

/** 레거시 부서 칸 — 사업장명 + 부서명(예: 디지털본부 Web Platform팀) */
export const formatBudgetDepartment = (info: {
  divisionName?: string | null;
  departmentName?: string | null;
}): string =>
  [info.divisionName, info.departmentName].filter(Boolean).join(' ');

export const parseBudgetAmount = (value: string): number =>
  Number(value.replace(/\D/g, '')) || 0;

/** SAP 금액 문자열 → 레거시 표기(정수 콤마). 비었으면 0 */
export const formatBudgetNumber = (
  value: string | null | undefined,
): string => {
  const n = parseInt(String(value ?? '').replace(/,/g, ''), 10);
  return Number.isNaN(n) ? '0' : n.toLocaleString('en-US');
};

export const findSelectedAccount = (
  state: SapBudgetIncreaseState | null | undefined,
): SapBudgetIncreaseAccount | null =>
  state?.accounts.find((a) => (a.costElement ?? '') === state.costElement) ??
  null;

export const parseSapBudgetIncreaseContent = (
  attributes: Record<string, unknown> | null | undefined,
): string | null => {
  const content = attributes?.content;
  return typeof content === 'string' && content.trim() ? content : null;
};

/** 레거시 — 계정 행이 있고 금액이 0보다 커야 상신 */
export const checkSapBudgetIncreaseBeforeSave = (
  state: SapBudgetIncreaseState | null | undefined,
): string | null => {
  if (state?.loading) return SAP_BUDGET_INCREASE_LOADING_MESSAGE;
  if (
    !findSelectedAccount(state) ||
    parseBudgetAmount(state?.amount ?? '') <= 0
  ) {
    return SAP_BUDGET_INCREASE_SAVE_FAILED_MESSAGE;
  }
  return null;
};

export const sapBudgetIncreaseKey = (
  state: SapBudgetIncreaseState | null | undefined,
): string => {
  if (!state || (!state.userChanged && !state.amount)) return '';
  return JSON.stringify([
    state.userChanged ? [state.yearMonth, state.costElement] : null,
    state.amount,
  ]);
};

/** 저장 attributes — content 는 작성 화면에 보인 표 HTML */
export const buildSapBudgetIncreaseAttributes = (
  state: SapBudgetIncreaseState | null | undefined,
  content: string,
): ApprovalAttributes | null =>
  state
    ? {
        yearMonth: state.yearMonth,
        costElement: state.costElement,
        amount: parseBudgetAmount(state.amount),
        content,
      }
    : null;
